import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import type { SyncedAgentConfig, SyncedThread, SyncedWorkspace } from '@/lib/desktop-shell/store';

/**
 * The user's conversations on Crowdex Cloud (`GET /api/cloud-link/conversations`),
 * marked as living there. Empty when the cloud is not linked or unreachable.
 */

type CloudBootstrap = {
  machines?: { id: string; platform: string }[];
  threads: Omit<SyncedThread, 'runtime' | 'onCloud'>[];
  workspaces: (SyncedWorkspace & { machineId?: string })[];
  agentConfigs: SyncedAgentConfig[];
  pullRequests: Record<string, PullRequestSummary | null>;
};

export type CloudConversations = {
  threads: SyncedThread[];
  workspaces: SyncedWorkspace[];
  agentConfigs: SyncedAgentConfig[];
  pullRequests: Record<string, PullRequestSummary | null>;
};

/** Pure: marks the cloud's remote conversations and workspaces as living there. */
function cloudConversations(bootstrap: CloudBootstrap | null): CloudConversations {
  if (bootstrap === null)
    return { threads: [], workspaces: [], agentConfigs: [], pullRequests: {} };
  const cloudMachines = new Set(
    (bootstrap.machines ?? [])
      .filter((machine) => machine.platform === 'cloud')
      .map((machine) => machine.id),
  );
  return {
    workspaces: bootstrap.workspaces
      .filter((entry) => cloudMachines.has(entry.machineId ?? ''))
      .map((entry) => ({
        ...entry,
        registered: true,
        runtime: 'cloud' as const,
        availableOnDevice: true,
        onCloud: true as const,
      })),
    threads: bootstrap.threads.map((thread) => ({
      ...thread,
      runtime: 'cloud' as const,
      onCloud: true as const,
    })),
    agentConfigs: bootstrap.agentConfigs,
    pullRequests: bootstrap.pullRequests,
  };
}

export async function loadCloudConversations(): Promise<CloudConversations> {
  const { bootstrap } = await apiFetch<{ bootstrap: CloudBootstrap | null }>(
    '/api/cloud-link/conversations',
    { cache: 'no-store' },
  );
  return cloudConversations(bootstrap);
}

/** Replaces the cloud's entries, keeping this session's server's own. */
export function withCloudEntries<T extends { id: string; onCloud?: true }>(
  current: readonly T[],
  entries: readonly T[],
): T[] {
  const ids = new Set(entries.map((entry) => entry.id));
  return [...current.filter((entry) => entry.onCloud !== true && !ids.has(entry.id)), ...entries];
}
